import { logError } from '@core/logging';
import { AuthContext, Language, languages } from '@core/shared';
import { AccountProfile, OptionsApi } from '@core/swagger-client';
import { Button, ContainerDefault, Dialog, ErrorMessage, ErrorMessagePage, getTranslations, Heading, Loader, Select, Upload, useFileDropzone } from '@core/ui';
import { useNavigate } from '@online-webinar/router';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMutation } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { serializeError } from 'serialize-error';

const ChunkSize = 25;

export const ImportAccountInvitesPage = () => {
    const canInviteAccounts = useContext(AuthContext).canInviteAccounts;
    if (!canInviteAccounts) {
        return <ErrorMessagePage statusCode={404} />;
    }

    const { t } = useTranslation();

    const nav = useNavigate();
    const [workBookError, setWorkBookError] = useState<string>();

    const [workSheetNames, setWorkSheetNames] = useState<string[]>([]);
    const [workSheets, setWorkSheets] = useState<{ [index: string]: string[][] }>({});

    const [chunkIndex, setChunkIndex] = useState<number>(-1);
    const [chunks, setChunks] = useState<AccountProfile[][]>([]);
    const [importing, setImporting] = useState<boolean>(false);
    const [selectedWorkSheet, setSelectedWorkSheet] = useState<string | null>(null);

    const [mapping, setMapping] = useState<string[]>([]);
    const [hasHeader, setHasHeader] = useState<boolean>(true);
    const [headers, setHeaders] = useState<string[]>([]);
    const [data, setData] = useState<string[][]>([]);

    const { getRootProps, getInputProps, isDragActive, rejectionCode } = useFileDropzone({
        multiple: false,
        onDrop: (acceptedFiles) => {
            if (acceptedFiles.length === 0) {
                return;
            }
            import('xlsx').then((xlsx) => {
                acceptedFiles[0].arrayBuffer().then((data) => {
                    try {
                        const workBook = xlsx.read(data, { type: 'array', dense: true, WTF: true });

                        const sheets: { [index: string]: string[][] } = {};
                        for (const sheetName of workBook.SheetNames) {
                            const sheet = workBook.Sheets[sheetName];
                            if (!sheet['!ref']) {
                                continue;
                            }
                            const range = xlsx.utils.decode_range(sheet['!ref']);

                            const sheetData: string[][] = [];
                            for (let row = range.s.r; row <= range.e.r; row++) {
                                const rowData: string[] = [];
                                for (let col = range.s.c; col <= range.e.c; col++) {
                                    const v = sheet['!data'] && sheet['!data'][row] && sheet['!data'][row][col] ? sheet['!data'][row][col].v : null;
                                    rowData.push(v ? v.toString() : '');
                                }
                                if (rowData.every((str) => !str?.trim())) {
                                    continue;
                                }
                                sheetData.push(rowData);
                            }

                            sheets[sheetName] = sheetData;
                        }

                        setWorkSheetNames(workBook.SheetNames);
                        setWorkSheets(sheets);

                        setWorkBookError(undefined);
                    } catch (error) {
                        setWorkSheetNames([]);
                        setWorkSheets({});

                        setWorkBookError(JSON.stringify(serializeError(error)));
                        logError('Error parsing workbook', { Error: serializeError(error) });
                    }
                });
            });
        },
        accept: {
            'application/vnd.ms-excel': ['.xls', '.xlsx', '.csv', '.ods', '.fods', '.txt']
        }
    });

    const getAllias = (fieldName: string, lang: Language): string[] => {
        fieldName = `Account.Label.${fieldName}`;

        const { original, patch } = getTranslations(lang);

        const labels = [];
        if (original[fieldName]) {
            labels.push(original[fieldName].toLocaleLowerCase());
        }
        if (patch[fieldName]) {
            labels.push(patch[fieldName].toLocaleLowerCase());
        }
        return labels;
    };

    const getAliasses = (fieldName: string): string[] => {
        let alliases: string[] = [];
        for (const lng in languages) {
            alliases = [...alliases, ...getAllias(fieldName, languages[lng].code)];
        }
        return alliases;
    };

    const fields = [
        {
            name: 'emailAddress',
            label: t('Account.Label.Email'),
            aliasses: [
                'email',
                'e-mail',
                'emailaddress',
                'e-mailaddress',
                'emailadres',
                'e-mailadres',
                'email address',
                'e-mail address',
                ...getAliasses('Email')
            ]
        },
        { name: 'nickname', label: t('Account.Label.Nickname'), aliasses: getAliasses('Nickname') },
        { name: 'firstName', label: t('Account.Label.FirstName'), aliasses: getAliasses('FirstName') },
        { name: 'lastName', label: t('Account.Label.LastName'), aliasses: getAliasses('LastName') },
        { name: 'companyName', label: t('Account.Label.CompanyName'), aliasses: getAliasses('CompanyName') },
        { name: 'countryCode', label: t('Account.Label.Country'), aliasses: getAliasses('Country') }
    ];

    useEffect(() => {
        // Check if selectedWorkSheet exists in workSheetnames
        let sheetName = selectedWorkSheet || '';
        if (!sheetName || !workSheetNames.includes(sheetName)) {
            if (workSheetNames.length > 0) {
                setSelectedWorkSheet(workSheetNames[0]);
                return;
            }

            setSelectedWorkSheet(null);
            setData([]);
            setHeaders([]);
            setMapping([]);
            return;
        }

        const sheetData = workSheets[sheetName];
        if (sheetData.length > 0) {
            let h: string[] = [];
            if (hasHeader) {
                h = sheetData[0];
            } else {
                for (let i = 0; i < sheetData[0].length; i++) {
                    h.push(`Column ${i + 1}`);
                }
            }
            setHeaders(h);

            const m: string[] = [];
            for (let i = 0; i < h.length; i++) {
                const field = fields.find((f) => {
                    if (f.label.toLocaleLowerCase() === h[i]?.trim()?.toLocaleLowerCase()) return true;

                    if (f.aliasses) {
                        return f.aliasses.includes(h[i]?.trim()?.toLocaleLowerCase());
                    }

                    return false;
                });
                if (field) {
                    m.push(field.name);
                } else {
                    m.push('');
                }
            }
            setMapping(m);
            setData(hasHeader ? sheetData.slice(1) : sheetData);
        } else {
            setData([]);
            setHeaders([]);
            setMapping([]);
        }
    }, [workSheetNames, workSheets, selectedWorkSheet, hasHeader]);

    const inviteMutation = useMutation(OptionsApi.accountInvites.invite());
    const importInvites = () => {
        if (!mapping.includes('emailAddress')) {
            setWorkBookError(t('ImportInvites.ErrorMessage.WorkBookError'));
            setTimeout(() => {
                setWorkBookError(undefined);
            }, 5000);
            return;
        }

        const invites = data.map((row) => {
            const invite: Partial<AccountProfile> = {};
            for (let i = 0; i < mapping.length; i++) {
                if (mapping[i]) {
                    invite[mapping[i] as keyof AccountProfile] = row[i];
                }
            }
            return invite as AccountProfile;
        });

        if (invites.length === 0) {
            return;
        }

        if (invites.length < ChunkSize) {
            inviteMutation.mutate(invites, {
                onSuccess: () => {
                    nav({ to: '/manage/account-invites' });
                }
            });
        } else {
            // Split invites into chunks of ChunkSize
            const chunks: AccountProfile[][] = [];
            while (invites.length > 0) {
                chunks.push(invites.splice(0, ChunkSize));
            }
            setChunks(chunks);
            setChunkIndex(0);
            setImporting(true);
        }
    };

    useEffect(() => {
        if (!importing || chunkIndex < 0 || chunkIndex >= chunks.length) {
            setImporting(false);
            return;
        }

        inviteMutation.mutate(chunks[chunkIndex], {
            onError: () => {
                setImporting(false);
                setChunks([]);
                setChunkIndex(-1);
            },
            onSuccess: () => {
                if (chunkIndex + 1 < chunks.length) {
                    setChunkIndex(chunkIndex + 1);
                } else {
                    setImporting(false);
                    setChunks([]);
                    setChunkIndex(-1);
                    nav({ to: '/manage/account-invites' });
                }
            }
        });
    }, [chunks, chunkIndex, importing]);

    return (
        <ContainerDefault>
            <Dialog open={importing} title={t('ImportInvites.Dialog.Title')}>
                <div className="flex flex-col gap-4 items-center">
                    <Loader />
                    {chunks.length > 0 && (
                        <p>
                            {t('ImportInvites.Dialog.Progress', {
                                current: chunkIndex * ChunkSize,
                                total: (chunks.length - 1) * ChunkSize + chunks[chunks.length - 1].length
                            })}
                        </p>
                    )}
                </div>
            </Dialog>

            <BackLink to="/manage/account-invites">{t('Button.BackToContent')}</BackLink>
            <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                {t('ImportInvites.PageHeading')}
            </Heading>

            <div className="max-w-4xl">
                {inviteMutation.error && <ErrorMessage apiError={inviteMutation.error} classNames="mb-4" />}
                <div
                    {...getRootProps()}
                    className={`group relative w-full min-h-64 ${isDragActive && 'bg-accent-lighter'} hover:bg-accent-lighter focus-visible:bg-accent-lighter border-2 border-dashed ${!isDragActive && 'border-gray-500'} ${isDragActive && 'border-accent'} hover:border-accent focus-visible:border-accent rounded-lg flex mb-4 md:mb-6 outline-hidden overflow-hidden p-6 transition-colors duration-150`}
                >
                    <div className="flex flex-col items-center m-auto">
                        <Upload classNames="size-12 mb-1 stroke-current group-hover:stroke-primary transition-color duration-200 ease-in-out" />
                        <p className="font-accent font-medium text-3xl mb-2">{isDragActive ? t('Generic.DragDrop.Active') : t('Generic.DragDrop')}</p>
                        <p>
                            <Trans components={{ span: <span className="border-b-2 border-primary" /> }} i18nKey="Generic.DragDrop.BrowseFiles" />
                        </p>
                        <p className="mt-4">
                            {t('Generic.DragDrop.AcceptedText')}
                            <span className="font-medium">{t('ImportInvites.DragDrop.AcceptedFileTypes')}</span>
                        </p>
                        {rejectionCode && <ErrorMessage errorSubject="Generic.FileRejection." errorCode={rejectionCode} classNames="mt-4" />}
                    </div>
                    <input {...getInputProps()} />
                </div>

                <p>{t('ImportInvites.Text.HasHeader')}</p>
                <div className="pl-10 mt-2 mb-10">
                    <input
                        type="checkbox"
                        role="switch"
                        id="hasHeader"
                        checked={hasHeader}
                        onChange={() => setHasHeader(!hasHeader)}
                        className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                    />
                    <label htmlFor="hasHeader" className="inline-block">
                        {t('ImportInvites.Label.HasHeader')}
                    </label>
                </div>

                {workSheetNames.length > 1 && (
                    <>
                        <p>{t('ImportInvites.Text.MultipleWorksheets')}</p>
                        <Select
                            id="worksheet"
                            label={t('ImportInvites.Label.Worksheet')}
                            value={selectedWorkSheet || ''}
                            onChange={(event) => setSelectedWorkSheet(event.target.value)}
                            classNames="max-w-80 mt-4 mb-10"
                        >
                            {workSheetNames.map((sheetName) => (
                                <option key={sheetName} value={sheetName}>
                                    {sheetName}
                                </option>
                            ))}
                        </Select>
                    </>
                )}

                {workBookError && <ErrorMessage statusMessage={workBookError} classNames="mb-4" />}

                {!workBookError && workSheetNames.length > 0 && data.length === 0 && (
                    <ErrorMessage statusMessage={t('ImportInvites.Text.NoDataRows')} classNames="mb-4" />
                )}

                {data && data.length > 0 && (
                    <>
                        <p className="mb-4">{t('ImportInvites.Text.MapColumns')}</p>
                        <Button isLoading={inviteMutation.isPending || importing} onClick={importInvites} variant="primary">
                            {t('ImportInvites.Button.Import')}
                        </Button>
                        <div className={`bg-white ${workBookError ? 'border-2 border-warning ' : ''}rounded-lg shadow-md table-responsive my-4`}>
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        {headers.map((header, index) => (
                                            <th key={index}>
                                                {!mapping[index] && <span>{header}</span>}
                                                <select
                                                    id={`mapping-${index}`}
                                                    value={mapping[index] ?? ''}
                                                    onChange={(event) =>
                                                        setMapping([...mapping.slice(0, index), event.target.value, ...mapping.slice(index + 1)])
                                                    }
                                                    className="ms-2"
                                                >
                                                    <option value="">{t('ImportInvites.MapOption.NotImported')}</option>
                                                    {fields.map((field) => (
                                                        <option key={field.name} value={field.name}>
                                                            {field.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((row, rowIndex) => (
                                        <tr key={rowIndex}>
                                            {row.map((cell, cellIndex) => (
                                                <td key={cellIndex}>{cell}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Button isLoading={inviteMutation.isPending} onClick={importInvites} variant="primary">
                            {t('ImportInvites.Button.Import')}
                        </Button>
                    </>
                )}
            </div>
        </ContainerDefault>
    );
};
